/**
 * 9.1 Reportes de Ventas (reforzado).
 * Filtros: dimensión + rango + región/RTC/canal/nivel precio.
 * Vistas: tabla agrupada con %, comparativos vs período anterior, margen.
 */
import { Fragment, useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtL, apiErr } from "@/lib/format";
import { toast } from "sonner";
import {
  ArrowUpIcon, ArrowDownIcon, FunnelIcon, DownloadIcon,
} from "@phosphor-icons/react";

const DIMENSIONES = [
  { k: "categoria", l: "Categoría (→ ítems)" },
  { k: "subcategoria", l: "Subcategoría" },
  { k: "item", l: "Ítem" },
  { k: "cliente", l: "Cliente" },
  { k: "proyecto", l: "Proyecto" },
  { k: "region", l: "Región" },
  { k: "rtc", l: "Vendedor (RTC)" },
  { k: "proveedor", l: "Línea / Proveedor" },
  { k: "nivel_precio", l: "Nivel de precio" },
  { k: "canal", l: "Canal (directa/RTC/contado)" },
  { k: "factura", l: "Factura / venta" },
];

const CORTES = [
  { k: "dia", l: "Día" },
  { k: "semana", l: "Semana" },
  { k: "mes", l: "Mes" },
  { k: "trimestre", l: "Trimestre" },
  { k: "anio", l: "Año" },
];

export default function SalesReports() {
  const [filtros, setFiltros] = useState({
    dimension: "categoria",
    desde: defaultDate(-30),
    hasta: defaultDate(0),
    region: "",
    rtc: "",
    canal: "",
    nivel_precio: "",
    proveedor: "",
    categoria: "",
    orden: "desc",
  });
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [clasif, setClasif] = useState(null);
  const [frec, setFrec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [corte, setCorte] = useState("dia");
  const [vsMeta, setVsMeta] = useState(null);
  const [margenes, setMargenes] = useState(null);
  const [nivelMargen, setNivelMargen] = useState("factura");
  const [expanded, setExpanded] = useState({});
  const [opts, setOpts] = useState({ proveedores: [], categorias: [] });
  // REP-01 — listas para dropdowns de región y RTC
  const [reps, setReps] = useState([]);
  const [regions, setRegions] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/representatives").catch(() => ({ data: [] })),
      api.get("/clients").catch(() => ({ data: [] })),
      api.get("/reports/sales/filtros-opciones").catch(() => ({ data: {} })),
    ]).then(([r, c, o]) => {
      setOpts({ proveedores: o.data?.proveedores || [], categorias: o.data?.categorias || [] });
      setReps((r.data || []).filter((x) => x.activo !== false));
      const set = new Set();
      (c.data || []).forEach((cl) => cl.region && set.add(cl.region));
      (r.data || []).forEach((rp) => rp.region && set.add(rp.region));
      setRegions(Array.from(set).sort());
    });
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filtros).filter(([_, v]) => v));
      const cross = Object.fromEntries(Object.entries({
        region: filtros.region, rtc: filtros.rtc, canal: filtros.canal, nivel_precio: filtros.nivel_precio,
        proveedor: filtros.proveedor, categoria: filtros.categoria,
      }).filter(([_, v]) => v));
      const rr = { desde: filtros.desde, hasta: filtros.hasta };
      const [d, t, c, f, vm] = await Promise.all([
        api.get(`/reports/sales/by-dimension`, { params }),
        api.get(`/reports/sales/timeline`, { params: { ...rr, corte, ...cross } }),
        api.get(`/reports/sales/clasificacion`, { params: { ...rr, region: filtros.region || undefined, rtc: filtros.rtc || undefined } }),
        api.get(`/reports/sales/frecuencia-clientes`, { params: { ...rr, region: filtros.region || undefined, rtc: filtros.rtc || undefined } }),
        api.get(`/reports/sales/vs-meta`, { params: rr }).catch(() => ({ data: null })),
      ]);
      setVsMeta(vm.data);
      setData(d.data);
      setTimeline(t.data);
      setClasif(c.data);
      setFrec(f.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filtros, corte]);
  useEffect(() => {
    const params = Object.fromEntries(Object.entries({ ...filtros, nivel: nivelMargen, orden: "desc", limit: 100 })
      .filter(([k, v]) => v && k !== "dimension"));
    api.get(`/reports/sales/margenes`, { params }).then((r) => setMargenes(r.data)).catch(() => setMargenes(null));
  }, [filtros, nivelMargen]);

  const exportCSV = () => {
    if (!data) return;
    const headers = ["Dimensión", "Total actual (L)", "% Participación", "Total anterior (L)", "Δ (L)", "Δ %", "Margen (L)", "Margen %", "# Ventas", "Ticket promedio"];
    const rows = data.rows.map((r) => [r.dimension, r.total, r.participacion_pct, r.total_anterior, r.delta, r.delta_pct, r.margen, r.margen_pct, r.ventas, r.ticket_promedio]);
    downloadCSV(`ventas_${filtros.dimension}_${filtros.desde}_${filtros.hasta}.csv`, headers, rows);
  };

  return (
    <div className="space-y-5">
      {/* Filtros */}
      <FiltersBar filtros={filtros} setFiltros={setFiltros} regions={regions} reps={reps} opts={opts} />

      {/* KPIs */}
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <Kpi label="Total período" value={fmtL(data.totales.actual)} accent="green" />
          <Kpi label="Período anterior" value={fmtL(data.totales.anterior)} accent="muted" />
          <Kpi label="Variación" value={`${data.totales.delta_pct >= 0 ? "+" : ""}${data.totales.delta_pct}%`} accent={data.totales.delta_pct >= 0 ? "green" : "red"} sub={fmtL(data.totales.delta)} />
          <Kpi label="# Facturas" value={data.totales.ventas_count} accent="yellow" />
        </div>
      )}

      {/* Tabla por dimensión */}
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border/60 flex justify-between items-center">
          <div>
            <h3 className="font-heading text-base">Ventas por {data?.dimension_label || filtros.dimension}</h3>
            <p className="text-[10px] text-text-muted uppercase tracking-wider mt-0.5">{data?.rango?.desde} → {data?.rango?.hasta} · vs. {data?.rango_anterior?.desde} → {data?.rango_anterior?.hasta}</p>
          </div>
          <button onClick={exportCSV} className="text-xs flex items-center gap-1 px-3 py-1.5 border border-border rounded-md hover:border-agro-green hover:text-agro-green" data-testid="sales-export">
            <DownloadIcon size={14} /> CSV
          </button>
        </div>
        <div className="overflow-x-auto max-h-[480px]">
          <table className="w-full">
            <thead className="bg-sidebar sticky top-0 z-10">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-3 pl-5">{data?.dimension_label || filtros.dimension}</th>
                <th className="p-3 text-right">Total</th>
                <th className="p-3 text-right">%</th>
                <th className="p-3 text-right">Anterior</th>
                <th className="p-3 text-right">Δ %</th>
                <th className="p-3 text-right">Margen</th>
                <th className="p-3 text-right">Mg%</th>
                <th className="p-3 text-right"># Ventas</th>
                <th className="p-3 pr-5 text-right">Ticket prom.</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="9" className="p-8 text-center text-text-muted">Cargando…</td></tr>
              : (data?.rows || []).length === 0 ? <tr><td colSpan="9" className="p-10 text-center text-text-muted">Sin datos en el período</td></tr>
              : data.rows.map((r, i) => (
                <Fragment key={i}>
                <tr className="border-t border-border/30 hover:bg-white/[0.02]" data-testid={`sales-row-${i}`}>
                  <td className="p-3 pl-5 text-sm">
                    {r.items?.length > 0 && (
                      <button onClick={() => setExpanded({ ...expanded, [r.dimension]: !expanded[r.dimension] })} className="mr-1 text-text-muted hover:text-agro-green" data-testid={`sales-expand-${i}`}>
                        {expanded[r.dimension] ? "▾" : "▸"}
                      </button>
                    )}
                    {r.dimension}
                  </td>
                  <td className="p-3 text-right text-sm font-medium">{fmtL(r.total)}</td>
                  <td className="p-3 text-right text-xs text-text-secondary">{r.participacion_pct}%</td>
                  <td className="p-3 text-right text-xs text-text-muted">{fmtL(r.total_anterior)}</td>
                  <td className="p-3 text-right text-xs">
                    <DeltaPill v={r.delta_pct} />
                  </td>
                  <td className="p-3 text-right text-xs">{fmtL(r.margen)}</td>
                  <td className={`p-3 text-right text-xs ${r.margen_pct >= 20 ? "text-agro-green" : r.margen_pct >= 10 ? "text-agro-yellow" : "text-red-400"}`}>{r.margen_pct}%</td>
                  <td className="p-3 text-right text-xs">{r.ventas}</td>
                  <td className="p-3 pr-5 text-right text-xs">{fmtL(r.ticket_promedio)}</td>
                </tr>
                {expanded[r.dimension] && (r.items || []).map((it, j) => (
                  <tr key={`${i}-${j}`} className="bg-app/40 text-xs">
                    <td className="p-2 pl-10 text-text-secondary">{it.item} <span className="text-text-muted">· {it.cantidad} u</span></td>
                    <td className="p-2 text-right">{fmtL(it.total)}</td>
                    <td className="p-2 text-right text-agro-green" title="% dentro del grupo">{it.pct_en_grupo}%</td>
                    <td colSpan="2" className="p-2 text-right text-text-muted">{it.pct_total}% del total</td>
                    <td className="p-2 text-right">{fmtL(it.margen)}</td>
                    <td className="p-2 text-right">{it.margen_pct}%</td>
                    <td colSpan="2" />
                  </tr>
                ))}
                </Fragment>
              ))}
            </tbody>
            {data?.rows?.length > 0 && (
              <tfoot className="border-t-2 border-border bg-sidebar/40">
                <tr>
                  <td className="p-3 pl-5 text-xs uppercase tracking-wider text-text-secondary font-semibold">Total</td>
                  <td className="p-3 text-right font-heading text-base text-agro-green">{fmtL(data.totales.actual)}</td>
                  <td className="p-3 text-right text-xs text-text-muted">100%</td>
                  <td className="p-3 text-right text-xs text-text-muted">{fmtL(data.totales.anterior)}</td>
                  <td className="p-3 text-right"><DeltaPill v={data.totales.delta_pct} /></td>
                  <td colSpan="4" className="p-3" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Tendencia */}
      <div className="bg-card border border-border/60 rounded-lg p-5">
        <div className="flex flex-wrap gap-2 justify-between items-center mb-4">
          <h3 className="font-heading text-base">Tendencia temporal</h3>
          <div className="flex flex-wrap gap-1">
            {CORTES.map((c) => (
              <button key={c.k} onClick={() => setCorte(c.k)} data-testid={`tl-corte-${c.k}`}
                className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border transition ${corte === c.k ? "bg-agro-green/10 text-agro-green border-agro-green/30" : "border-border text-text-muted hover:text-text-primary"}`}>
                {c.l}
              </button>
            ))}
          </div>
        </div>
        <Sparkline points={timeline?.puntos || []} />
      </div>

      {/* Ventas vs meta */}
      {vsMeta && (
        <div className="bg-card border border-border/60 rounded-lg p-5" data-testid="sales-vs-meta">
          <h3 className="font-heading text-base mb-3">Ventas vs meta ({vsMeta.rango.desde} → {vsMeta.rango.hasta})</h3>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <MetaTable title="Total" rows={[vsMeta.total]} />
            <MetaTable title="Por región" rows={vsMeta.regiones} />
            <MetaTable title="Por RTC" rows={vsMeta.rtcs} />
          </div>
        </div>
      )}

      {/* Margen con costo PEPS real */}
      {margenes && (
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden" data-testid="sales-margenes">
          <div className="p-4 border-b border-border/60 flex justify-between items-center">
            <div>
              <h3 className="font-heading text-base">Margen (costo PEPS real)</h3>
              <p className="text-[10px] text-text-muted">Neto {fmtL(margenes.totales.neto)} · Costo {fmtL(margenes.totales.costo)} · Margen {fmtL(margenes.totales.margen)} ({margenes.totales.margen_pct}%)</p>
            </div>
            <select value={nivelMargen} onChange={(e) => setNivelMargen(e.target.value)} className="text-xs bg-app border border-border rounded-md px-2 py-1" data-testid="margen-nivel">
              <option value="factura">Por factura</option><option value="item">Por ítem</option><option value="linea">Por venta (línea)</option>
            </select>
          </div>
          <div className="overflow-x-auto max-h-80">
            <table className="w-full text-xs">
              <thead className="bg-sidebar/60 sticky top-0">
                <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                  <th className="p-2">{nivelMargen === "item" ? "Ítem" : "Documento"}</th>
                  <th className="p-2">{nivelMargen === "item" ? "Categoría" : "Cliente"}</th>
                  <th className="p-2 text-right">Neto</th><th className="p-2 text-right">Costo</th>
                  <th className="p-2 text-right">Margen</th><th className="p-2 text-right">%</th>
                </tr>
              </thead>
              <tbody>
                {margenes.rows.map((r, i) => (
                  <tr key={i} className="border-t border-border/30">
                    <td className="p-2">{nivelMargen === "item" ? r.item : `${r.numero}${nivelMargen === "linea" ? ` · ${r.item}` : ""}`}{r.costo_estimado && <span className="ml-1 text-[9px] text-agro-yellow" title="Costo estimado (sin PEPS registrado)">est.</span>}</td>
                    <td className="p-2 text-text-secondary">{nivelMargen === "item" ? r.categoria : r.cliente}</td>
                    <td className="p-2 text-right">{fmtL(r.neto)}</td>
                    <td className="p-2 text-right text-text-muted">{fmtL(r.costo)}</td>
                    <td className={`p-2 text-right font-medium ${r.margen >= 0 ? "text-agro-green" : "text-red-400"}`}>{fmtL(r.margen)}</td>
                    <td className="p-2 text-right">{r.margen_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Clasificación + Frecuencia */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {clasif && (
          <ClasificacionBox title="Clientes (Pareto)" data={clasif.clientes} />
        )}
        {clasif && (
          <ClasificacionBox title="Productos (Pareto)" data={clasif.productos} />
        )}
      </div>

      {/* Frecuencia + Inactivos */}
      {frec && (
        <div className="bg-card border border-border/60 rounded-lg p-5">
          <h3 className="font-heading text-base mb-3">Frecuencia de compra · Clientes inactivos</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <MiniStat label="Activos (≤30d)" value={frec.totales.activos} color="green" />
            <MiniStat label="Regulares (31-60d)" value={frec.totales.regulares} color="yellow" />
            <MiniStat label="En riesgo (61-90d)" value={frec.totales.en_riesgo} color="orange" />
            <MiniStat label="Inactivos (+90d)" value={frec.totales.inactivos} color="red" />
          </div>
          {frec.inactivos.length > 0 && (
            <div className="overflow-x-auto max-h-72">
              <table className="w-full text-xs">
                <thead className="bg-sidebar/50">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                    <th className="p-2">Cliente</th>
                    <th className="p-2 text-right">Última compra</th>
                    <th className="p-2 text-right">Días sin comprar</th>
                    <th className="p-2 text-right">Total histórico</th>
                  </tr>
                </thead>
                <tbody>
                  {frec.inactivos.slice(0, 30).map((i, k) => (
                    <tr key={k} className="border-t border-border/30">
                      <td className="p-2">{i.cliente_nombre}</td>
                      <td className="p-2 text-right text-text-secondary">{i.ultima_compra || "—"}</td>
                      <td className={`p-2 text-right ${i.dias_sin_compra > 90 ? "text-red-400" : "text-agro-yellow"} font-medium`}>{i.dias_sin_compra}d</td>
                      <td className="p-2 text-right">{fmtL(i.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FiltersBar({ filtros, setFiltros, regions = [], reps = [], opts = { proveedores: [], categorias: [] } }) {
  return (
    <div className="bg-card border border-border/60 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3 text-xs text-text-secondary">
        <FunnelIcon size={14} /> Filtros del reporte
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        <Field label="Agrupar por">
          <select value={filtros.dimension} onChange={(e) => setFiltros({...filtros, dimension: e.target.value})} data-testid="sales-dim" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
            {DIMENSIONES.map((d) => <option key={d.k} value={d.k}>{d.l}</option>)}
          </select>
        </Field>
        <Field label="Desde">
          <input type="date" value={filtros.desde} onChange={(e) => setFiltros({...filtros, desde: e.target.value})} data-testid="sales-desde" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm" />
        </Field>
        <Field label="Hasta">
          <input type="date" value={filtros.hasta} onChange={(e) => setFiltros({...filtros, hasta: e.target.value})} data-testid="sales-hasta" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm" />
        </Field>
        <Field label="Región">
          <select value={filtros.region} onChange={(e) => setFiltros({...filtros, region: e.target.value})} data-testid="sales-region" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
            <option value="">— Todas —</option>
            {regions.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="RTC">
          <select value={filtros.rtc} onChange={(e) => setFiltros({...filtros, rtc: e.target.value})} data-testid="sales-rtc" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
            <option value="">— Todos —</option>
            {reps.map((r) => <option key={r.id} value={r.id}>{r.codigo} · {r.nombre}</option>)}
          </select>
        </Field>
        <Field label="Canal">
          <select value={filtros.canal} onChange={(e) => setFiltros({...filtros, canal: e.target.value})} className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
            <option value="">Todos</option><option value="directa">Directa</option><option value="rtc">Por RTC</option>
          </select>
        </Field>
        <Field label="Nivel precio">
          <select value={filtros.nivel_precio} onChange={(e) => setFiltros({...filtros, nivel_precio: e.target.value})} className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
            <option value="">Todos</option>
            <option value="P0">P0 (especial)</option>
            <option value="P1">P1</option><option value="P2">P2</option><option value="P3">P3</option><option value="P4">P4</option>
          </select>
        </Field>
        <Field label="Línea / proveedor">
          <select value={filtros.proveedor} onChange={(e) => setFiltros({...filtros, proveedor: e.target.value})} data-testid="sales-proveedor" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
            <option value="">Todos</option>
            {opts.proveedores.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
        <Field label="Categoría">
          <select value={filtros.categoria} onChange={(e) => setFiltros({...filtros, categoria: e.target.value})} data-testid="sales-categoria" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
            <option value="">Todas</option>
            {opts.categorias.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Orden">
          <select value={filtros.orden} onChange={(e) => setFiltros({...filtros, orden: e.target.value})} data-testid="sales-orden" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
            <option value="desc">Mayores primero</option><option value="asc">Menores primero</option>
          </select>
        </Field>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[9px] uppercase tracking-wider text-text-muted mb-1 block">{label}</label>
      {children}
    </div>
  );
}

function Kpi({ label, value, sub, accent }) {
  const c = accent === "green" ? "text-agro-green" : accent === "red" ? "text-red-400" : accent === "yellow" ? "text-agro-yellow" : "text-text-secondary";
  return (
    <div className="bg-card border border-border/60 rounded-lg p-4">
      <div className="text-[9px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={`font-heading text-2xl mt-0.5 ${c}`}>{value}</div>
      {sub && <div className="text-[10px] text-text-muted mt-1">{sub}</div>}
    </div>
  );
}

function MiniStat({ label, value, color }) {
  const c = { green: "text-agro-green", yellow: "text-agro-yellow", orange: "text-orange-400", red: "text-red-400" }[color];
  return (
    <div className="bg-app/50 border border-border/40 rounded-md p-2.5">
      <div className="text-[9px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={`font-heading text-xl ${c}`}>{value}</div>
    </div>
  );
}

function DeltaPill({ v }) {
  if (v == null) return <span className="text-text-muted">—</span>;
  const up = v >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded ${up ? "text-agro-green bg-agro-green/10" : "text-red-400 bg-red-500/10"} text-[10px] font-medium`}>
      {up ? <ArrowUpIcon size={10} weight="bold" /> : <ArrowDownIcon size={10} weight="bold" />}
      {Math.abs(v).toFixed(1)}%
    </span>
  );
}

function Sparkline({ points }) {
  if (!points || points.length === 0) {
    return <div className="text-center text-text-muted text-sm py-10">Sin datos</div>;
  }
  const max = Math.max(...points.map((p) => p.total)) || 1;
  const W = 800, H = 180, pad = 20;
  const stepX = points.length > 1 ? (W - pad * 2) / (points.length - 1) : 0;
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${pad + i * stepX},${H - pad - (p.total / max) * (H - pad * 2)}`).join(" ");
  const areaPath = path + ` L${pad + (points.length - 1) * stepX},${H - pad} L${pad},${H - pad} Z`;
  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-44">
        <defs>
          <linearGradient id="ag-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#02BF44" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#02BF44" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#ag-grad)" />
        <path d={path} fill="none" stroke="#02BF44" strokeWidth="2" />
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={pad + i * stepX} cy={H - pad - (p.total / max) * (H - pad * 2)} r="3" fill="#02BF44" />
          </g>
        ))}
      </svg>
      <div className="flex justify-between text-[10px] text-text-muted px-5">
        {points.map((p, i) => (i === 0 || i === points.length - 1 || i % Math.max(1, Math.floor(points.length / 8)) === 0) && (
          <span key={i}>{p.periodo}</span>
        ))}
      </div>
    </div>
  );
}

function MetaTable({ title, rows }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">{title}</div>
      <div className="overflow-x-auto"><table className="w-full text-xs">
        <tbody>
          {(rows || []).length === 0 ? <tr><td className="p-2 text-text-muted">Sin datos</td></tr> : rows.map((r, i) => (
            <tr key={i} className="border-t border-border/30">
              <td className="p-1.5 truncate max-w-[140px]">{r.nombre}</td>
              <td className="p-1.5 text-right">{fmtL(r.real)}</td>
              <td className="p-1.5 text-right text-text-muted">{r.meta ? fmtL(r.meta) : "sin meta"}</td>
              <td className={`p-1.5 text-right font-medium ${r.cumplimiento_pct == null ? "text-text-muted" : r.cumplimiento_pct >= 100 ? "text-agro-green" : r.cumplimiento_pct >= 80 ? "text-agro-yellow" : "text-red-400"}`}>
                {r.cumplimiento_pct == null ? "—" : `${r.cumplimiento_pct}%`}
              </td>
            </tr>
          ))}
        </tbody>
      </table></div>
    </div>
  );
}

function ClasificacionBox({ title, data }) {
  const [modo, setModo] = useState("mayores");
  return (
    <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
      <div className="p-4 border-b border-border/60">
        <div className="flex justify-between items-center">
          <h3 className="font-heading text-base">{title}</h3>
          <div className="flex gap-1">
            {["mayores", "menores"].map((m) => (
              <button key={m} onClick={() => setModo(m)} data-testid={`clasif-${m}`}
                className={`text-[10px] uppercase px-2 py-0.5 rounded border ${modo === m ? "bg-agro-green/10 text-agro-green border-agro-green/30" : "border-border text-text-muted"}`}>{m}</button>
            ))}
          </div>
        </div>
        <div className="text-[10px] text-text-muted mt-0.5">
          {data.que_hacen_80pct} de {data.total} hacen el 80% de ventas
        </div>
      </div>
      <div className="max-h-72 overflow-y-auto">
        <table className="w-full text-xs">
          <tbody>
            {(modo === "mayores" ? data.mayores : (data.menores || [])).slice(0, 15).map((r, i) => (
              <tr key={i} className="border-b border-border/30">
                <td className="p-2 pl-4 text-text-muted w-6">#{i + 1}</td>
                <td className="p-2 truncate max-w-[180px]">{r.nombre}</td>
                <td className="p-2 text-right font-medium">{fmtL(r.total)}</td>
                <td className={`p-2 text-right w-12 ${modo === "mayores" ? "text-agro-green" : "text-red-400"}`}>{r.pct}%</td>
                <td className="p-2 text-right text-text-muted w-14">{r.acumulado_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function defaultDate(daysOffset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + daysOffset);
  return d.toISOString().slice(0, 10);
}

function downloadCSV(filename, headers, rows) {
  const csv = [headers.join(","), ...rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}
